import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { ROUTES_MAP } from 'app/shared/utils/routes';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';

const BASE_MCP_CLIENT_REDIRECT_SCHEMES = new Set([
  'https',
  'cursor',
  'vscode',
  'claude',
  'codex',
]);

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

export const rememberMcpOAuthState = (state: string) => {
  localStorage.setItem(LocalStorageKeys.MCP_OAUTH_STATE, state);
};

export const getMcpOAuthState = (): string | null => {
  return localStorage.getItem(LocalStorageKeys.MCP_OAUTH_STATE);
};

export const clearMcpOAuthState = () => {
  localStorage.removeItem(LocalStorageKeys.MCP_OAUTH_STATE);
};

export const buildMcpConsentPath = (state: string) => {
  const params = queryString.stringify({ mcp_oauth_state: state });
  return `${ROUTES_MAP.mcpConsent.path}?${params}`;
};

// Query-param only (no localStorage fallback) — used before login when nothing
// has been persisted yet and we only want to forward the inbound state param.
export const getMcpOAuthStateFromSearch = (search: string): string | null => {
  const queryState = queryString.parse(search).mcp_oauth_state;
  if (typeof queryState === 'string' && queryState.length > 0) {
    return queryState;
  }
  return null;
};

export const captureMcpOAuthStateFromSearch = (search: string) => {
  const state = getMcpOAuthStateFromSearch(search);
  if (state) {
    rememberMcpOAuthState(state);
  }
};

export const getPendingMcpOAuthState = (search: string): string | null => {
  return getMcpOAuthStateFromSearch(search) ?? getMcpOAuthState();
};

/** Match Patronus MCP OAuth redirect_uri scheme allowlist. */
export const isSafeMcpClientRedirect = (url: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  const scheme = parsed.protocol.replace(':', '');
  if (BASE_MCP_CLIENT_REDIRECT_SCHEMES.has(scheme)) {
    return true;
  }

  return scheme === 'http' && LOOPBACK_HOSTS.has(parsed.hostname);
};

export const isWebMcpClientRedirect = (url: string): boolean => {
  if (!isSafeMcpClientRedirect(url)) {
    return false;
  }
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

export const useMcpOAuthPendingRedirect = (isAuthenticated: boolean) => {
  const history = useHistory();
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (pathname === ROUTES_MAP.mcpConsent.path) {
      return;
    }
    captureMcpOAuthStateFromSearch(search);
    const pendingState = getPendingMcpOAuthState(search);
    if (!pendingState) {
      return;
    }
    history.replace(buildMcpConsentPath(pendingState));
  }, [history, isAuthenticated, pathname, search]);
};
